import React, { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  Select,
  InputNumber,
  DatePicker,
  Input,
  Alert,
  message,
  Space,
  Tag,
  Button,
  Typography,
  Upload,
} from 'antd';
import type { UploadFile, RcFile } from 'antd/es/upload/interface';
import {
  SwapOutlined,
  UserOutlined,
  BankOutlined,
  PlusOutlined,
  UploadOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useCompany } from '../../../contexts/CompanyContext';
import { useConfig } from '../../../contexts/ConfigContext';
import useAuth from '../../../hooks/useAuth';
import { useSecureRequest } from '../../../hooks/useSecureRequest';
import {
  useTreasuryAccounts,
  useContacts,
  usePeriodLock,
  mutateContacts,
} from '../../../hooks/useAccounting';
import {
  TreasuryMovementType,
  isPartnerContact,
  TreasuryMovementAttachment,
} from '../../../services/accountingService';
import fileStorageService, {
  MOVEMENT_ATTACHMENTS_BUCKET,
} from '../../../services/fileStorageService';
import { UnifiedContactModal } from '@ryo/ui';
import { formatCurrency } from '../journal-entries/journalEntriesHelpers';

const { Option } = Select;
const { Text } = Typography;

interface CreateTreasuryMovementModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  defaultMovementType?: TreasuryMovementType;
  defaultContactId?: number | null;
  suggestedAmount?: number;
}

const MOVEMENT_TYPE_INFO: Record<
  TreasuryMovementType,
  { label: string; description: string; color: string }
> = {
  INTERNAL_TRANSFER: {
    label: 'Transferencia Interna entre Cuentas',
    description: 'Traspaso de fondos entre cuentas bancarias, billeteras o cajas de la empresa (Ej: Bancolombia a Nequi o Caja General a Banco).',
    color: '#1890ff',
  },
  PARTNER_REIMBURSEMENT: {
    label: 'Reembolso a Socio (Pago de Pasivo 2355)',
    description: 'La empresa le reembolsa dinero al socio por gastos o compras que pagó previamente de su bolsillo. Disminuye la deuda con el socio.',
    color: '#52c41a',
  },
  PARTNER_ADVANCE: {
    label: 'Anticipo / Gasto Personal del Socio (CxC 1325)',
    description: 'La empresa entrega dinero al socio o paga una obligación personal del socio. Se registra como Cuenta por Cobrar al Socio.',
    color: '#fa8c16',
  },
  PARTNER_RETURN: {
    label: 'Devolución de Anticipo por el Socio',
    description: 'El socio reintegra dinero a la empresa por un anticipo o retiro previo. Disminuye el activo 1325 y entra dinero al banco/caja.',
    color: '#13c2c2',
  },
  PARTNER_LOAN: {
    label: 'Préstamo / Aporte del Socio a la Empresa',
    description: 'El socio inyecta liquidez en efectivo o transferencia a las cuentas de la empresa. Incrementa la deuda con el socio (2355).',
    color: '#722ed1',
  },
  PARTNER_NETTING: {
    label: 'Compensación de Saldos (Cruce 1325 vs 2355)',
    description: 'Cruce contable directo entre la deuda de la empresa (2355) y los anticipos pendientes del socio (1325). No involucra bancos.',
    color: '#eb2f96',
  },
};

export const CreateTreasuryMovementModal: React.FC<CreateTreasuryMovementModalProps> = ({
  open,
  onClose,
  onSuccess,
  defaultMovementType = 'INTERNAL_TRANSFER',
  defaultContactId = null,
  suggestedAmount,
}) => {
  const { company } = useCompany();
  const { config } = useConfig();
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key);
  const { securePost, loading } = useSecureRequest();
  const [form] = Form.useForm();

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);

  const { accounts } = useTreasuryAccounts({
    company_id: company.current?.id,
    company_uid: company.current?.uid || undefined,
  });

  const { contacts } = useContacts({
    company_id: company.current?.id,
    company_uid: company.current?.uid || undefined,
  });

  const { lockDate } = usePeriodLock({
    company_id: company.current?.id,
  });

  const [movementType, setMovementType] = useState<TreasuryMovementType>(defaultMovementType);

  // Configured partner accounts in treasury
  const configuredPartnerAccounts = React.useMemo(() => {
    return accounts.filter((a) => a.type === 'PARTNER' && a.contact_id);
  }, [accounts]);

  const configuredPartnerContactIds = React.useMemo(() => {
    return new Set(
      configuredPartnerAccounts
        .map((a) => a.contact_id)
        .filter((id): id is number => typeof id === 'number')
    );
  }, [configuredPartnerAccounts]);

  // Valid contacts for partner operations:
  // Must have a valid, non-empty document_number AND have at least the PARTNER role or be configured in treasury
  const eligiblePartnerContacts = React.useMemo(() => {
    const seen = new Set();
    return contacts.filter((c) => {
      if (!c?.id) return false;
      const doc = String(c.document_number || '').trim();
      if (!doc) return false;
      const isPartner =
        isPartnerContact(c, configuredPartnerContactIds) || (defaultContactId && c.id === defaultContactId);
      // Strictly exclude contacts without the PARTNER role
      if (!isPartner) return false;
      if (seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });
  }, [contacts, configuredPartnerContactIds, defaultContactId]);

  const [partnerModalOpen, setPartnerModalOpen] = useState(false);

  const handleSavePartnerContact = async (values: any) => {
    const dataRequest: any = {
      company: company.current?.uid,
      company_id: company.current?.id,
      module: 'ACCOUNTING',
      contact: {
        ...values,
        company_id: company.current?.id,
        roles: [{ role_type: 'PARTNER' }],
      },
    };
    const res = await securePost('/user/contact/create-contact', dataRequest);
    if (res?.status && res.status !== 200 && res.status !== 201) {
      throw new Error(res?.message || 'Error al guardar el socio');
    }
    message.success('Socio creado exitosamente');
    await mutateContacts(company.current?.id);
    const newContact = res?.contact || res;
    if (newContact?.id) {
      form.setFieldsValue({ contact_id: newContact.id });
    }
    return newContact;
  };

  // Separate into configured partners and other eligible contacts
  const { configuredPartnersList, otherContactsList } = React.useMemo(() => {
    const configured: typeof eligiblePartnerContacts = [];
    const others: typeof eligiblePartnerContacts = [];

    for (const c of eligiblePartnerContacts) {
      if (configuredPartnerContactIds.has(c.id)) {
        configured.push(c);
      } else {
        others.push(c);
      }
    }
    return { configuredPartnersList: configured, otherContactsList: others };
  }, [eligiblePartnerContacts, configuredPartnerContactIds]);

  // Filter company liquid accounts (cash, bank, wallet)
  const companyLiquidAccounts = accounts.filter(
    (a) => a.type === 'CASH' || a.type === 'BANK' || a.type === 'WALLET'
  );

  useEffect(() => {
    if (open) {
      setFileList([]);
      const type = defaultMovementType || 'INTERNAL_TRANSFER';
      setMovementType(type);
      form.setFieldsValue({
        movement_type: type,
        contact_id: defaultContactId || undefined,
        amount: suggestedAmount && suggestedAmount > 0 ? suggestedAmount : undefined,
        movement_date: dayjs().startOf('minute'),
        reference: '',
        notes: '',
        source_account_id: undefined,
        destination_account_id: undefined,
      });
    }
  }, [open, defaultMovementType, defaultContactId, suggestedAmount, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const movementDateStr = values.movement_date
        ? dayjs(values.movement_date).startOf('minute').format()
        : dayjs().startOf('minute').format();

      // Upload any attachments to Supabase Storage before persisting movement
      let uploadedAttachments: TreasuryMovementAttachment[] = [];
      if (fileList.length > 0) {
        setUploadingFiles(true);
        try {
          for (const item of fileList) {
            if ((item as any).url) {
              uploadedAttachments.push({
                name: item.name,
                url: (item as any).url,
                storage_path: (item as any).storage_path,
                type: item.type,
                size: item.size,
              });
              continue;
            }
            if (item.originFileObj) {
              const cleanName = item.name.replace(/[^a-zA-Z0-9.-]/g, '_');
              const storagePath = `company-${company.current?.id || 'default'}/treasury/${Date.now()}-${cleanName}`;
              const { url, storagePath: finalPath } = await fileStorageService.uploadFile(
                tokenData,
                MOVEMENT_ATTACHMENTS_BUCKET,
                storagePath,
                item.originFileObj
              );
              uploadedAttachments.push({
                name: item.name,
                url,
                storage_path: finalPath,
                type: item.type,
                size: item.size,
              });
            }
          }
        } catch (uploadErr: any) {
          console.error('Error subiendo soporte bancario:', uploadErr);
          message.error(`Error al subir el comprobante: ${uploadErr.message || 'Fallo de almacenamiento'}`);
          setUploadingFiles(false);
          return;
        } finally {
          setUploadingFiles(false);
        }
      }

      const payload = {
        company_id: company.current?.id,
        company: company.current?.uid || company.current?.id,
        movement_type: values.movement_type,
        amount: Number(values.amount),
        movement_date: movementDateStr,
        reference: values.reference || undefined,
        notes: values.notes || undefined,
        attachments: uploadedAttachments,
        source_account_id: values.source_account_id || undefined,
        destination_account_id: values.destination_account_id || undefined,
        from_treasury_account_id: values.source_account_id || undefined,
        to_treasury_account_id: values.destination_account_id || undefined,
        partner_contact_id: values.contact_id || undefined,
        contact_id: values.contact_id || undefined,
      };

      const res = await securePost('/accounting/treasury-movements/create-movement', payload);

      const isSuccess =
        (res?.status === 200 || res?.status === 201) &&
        res?.success !== false &&
        res?.data?.success !== false;

      if (isSuccess) {
        message.success('Operación de tesorería y comprobante contable creados con éxito');
        form.resetFields();
        setFileList([]);
        onSuccess();
        onClose();
      } else {
        const errorMsg =
          res?.data?.message || res?.message || 'Error al procesar la operación de tesorería';
        message.error(errorMsg);
      }
    } catch (err: any) {
      if (err?.errorFields) return;
      console.error('Error creating treasury movement:', err);
      message.error(err?.message || 'Error al procesar la operación');
    }
  };

  const typeConfig = MOVEMENT_TYPE_INFO[movementType] || MOVEMENT_TYPE_INFO.INTERNAL_TRANSFER;

  return (
    <>
      <Modal
      title={
        <Space>
          <SwapOutlined style={{ color: '#1890ff', fontSize: 20 }} />
          <span>Registrar Operación de Tesorería</span>
        </Space>
      }
      open={open}
      onCancel={() => {
        setFileList([]);
        onClose();
      }}
      onOk={handleSubmit}
      confirmLoading={loading || uploadingFiles}
      okText="Registrar y Asentar Contabilidad"
      cancelText="Cancelar"
      width={620}
    >
      <Alert
        message={typeConfig.label}
        description={typeConfig.description}
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Form form={form} layout="vertical">
        <Form.Item
          label="Tipo de Operación"
          name="movement_type"
          rules={[{ required: true, message: 'Seleccione el tipo de operación' }]}
        >
          <Select
            onChange={(val) => {
              setMovementType(val);
              form.setFieldsValue({
                source_account_id: undefined,
                destination_account_id: undefined,
              });
            }}
          >
            {Object.entries(MOVEMENT_TYPE_INFO).map(([key, info]) => (
              <Option key={key} value={key}>
                <Space>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: info.color,
                    }}
                  />
                  <span>{info.label}</span>
                </Space>
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* Contact / Partner field for partner operations */}
        {movementType !== 'INTERNAL_TRANSFER' && (
          <Form.Item
            label={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <span>Socio / Accionista</span>
                <Button
                  type="link"
                  size="small"
                  icon={<PlusOutlined />}
                  style={{ padding: 0, height: 'auto', fontSize: 12, marginLeft: 16 }}
                  onClick={() => setPartnerModalOpen(true)}
                >
                  + Nuevo Socio
                </Button>
              </div>
            }
            name="contact_id"
            rules={[{ required: true, message: 'Seleccione el socio involucrado' }]}
            tooltip="Solo se muestran contactos con rol PARTNER (Socio / Accionista) y Cédula/NIT registrado"
            extra={
              eligiblePartnerContacts.length === 0
                ? 'No se encontraron contactos con rol de Socio y documento registrado. Puede crearlo con el botón "+ Nuevo Socio".'
                : undefined
            }
          >
            <Select
              showSearch
              placeholder="Buscar socio por nombre o documento..."
              optionFilterProp="children"
            >
              {configuredPartnersList.length > 0 && (
                <Select.OptGroup label="Socios Configurados en Tesorería">
                  {configuredPartnersList.map((c) => (
                    <Option key={c.id} value={c.id}>
                      <Space>
                        <UserOutlined style={{ color: '#13c2c2' }} />
                        <span style={{ fontWeight: 500 }}>{c.name}</span>
                        {c.document_number && (
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            ({c.document_number})
                          </Text>
                        )}
                        <Tag color="cyan" style={{ fontSize: 10, padding: '0 4px', margin: 0 }}>
                          Socio
                        </Tag>
                      </Space>
                    </Option>
                  ))}
                </Select.OptGroup>
              )}

              {otherContactsList.length > 0 && (
                <Select.OptGroup
                  label={
                    configuredPartnersList.length > 0
                      ? 'Otros Contactos con Documento (NIT / CC)'
                      : 'Contactos con Documento'
                  }
                >
                  {otherContactsList.map((c) => (
                    <Option key={c.id} value={c.id}>
                      <Space>
                        <UserOutlined />
                        <span>{c.name}</span>
                        {c.document_number && (
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            ({c.document_number})
                          </Text>
                        )}
                      </Space>
                    </Option>
                  ))}
                </Select.OptGroup>
              )}
            </Select>
          </Form.Item>
        )}

        {/* Source Account: for transfers, reimbursements, advances */}
        {['INTERNAL_TRANSFER', 'PARTNER_REIMBURSEMENT', 'PARTNER_ADVANCE'].includes(movementType) && (
          <Form.Item
            label="Cuenta Origen de Fondos (Sale dinero de la empresa)"
            name="source_account_id"
            rules={[{ required: true, message: 'Seleccione la cuenta bancaria o caja de origen' }]}
          >
            <Select placeholder="Seleccionar cuenta de la empresa...">
              {companyLiquidAccounts
                .filter((a) => a.allow_outflows !== false)
                .map((a) => (
                  <Option key={a.id} value={a.id}>
                    <Space>
                      <BankOutlined />
                      <span>{a.name}</span>
                      {a.bank_name && (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          ({a.bank_name})
                        </Text>
                      )}
                      <Text strong style={{ fontSize: 11, color: '#1890ff' }}>
                        Saldo: {formatCurrency(a.current_balance || 0)}
                      </Text>
                    </Space>
                  </Option>
                ))}
            </Select>
          </Form.Item>
        )}

        {/* Destination Account: for transfers, returns, loans */}
        {['INTERNAL_TRANSFER', 'PARTNER_RETURN', 'PARTNER_LOAN'].includes(movementType) && (
          <Form.Item
            label="Cuenta Destino de Fondos (Ingresa dinero a la empresa)"
            name="destination_account_id"
            rules={[
              { required: true, message: 'Seleccione la cuenta bancaria o caja de destino' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (movementType === 'INTERNAL_TRANSFER' && value && value === getFieldValue('source_account_id')) {
                    return Promise.reject(new Error('La cuenta de destino no puede ser igual a la cuenta origen'));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Select placeholder="Seleccionar cuenta de destino de la empresa...">
              {companyLiquidAccounts
                .filter((a) => a.allow_inflows !== false)
                .map((a) => (
                  <Option key={a.id} value={a.id}>
                    <Space>
                      <BankOutlined />
                      <span>{a.name}</span>
                      {a.bank_name && (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          ({a.bank_name})
                        </Text>
                      )}
                    </Space>
                  </Option>
                ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item
          label="Monto de la Operación"
          name="amount"
          rules={[
            { required: true, message: 'Ingrese el monto' },
            { type: 'number', min: 1, message: 'El monto debe ser mayor a cero' },
          ]}
        >
          <InputNumber
            prefix="$"
            style={{ width: '100%' }}
            formatter={(val) => (val ? `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '')}
            parser={(val) => (val ? Number(val.replace(/\$\s?|(,*)/g, '')) : 0)}
            placeholder="Ingrese el monto en pesos"
          />
        </Form.Item>

        <Form.Item
          label="Fecha de la Operación"
          name="movement_date"
          tooltip={
            lockDate
              ? `Periodo contable cerrado hasta el ${dayjs(lockDate).format('DD/MM/YYYY')}. Solo se permiten operaciones posteriores.`
              : undefined
          }
          rules={[
            { required: true, message: 'Seleccione la fecha' },
            {
              validator: (_, value) => {
                if (value && lockDate) {
                  const valDay = dayjs(value);
                  const lockDay = dayjs(lockDate);
                  if (valDay.isBefore(lockDay, 'day') || valDay.isSame(lockDay, 'day')) {
                    return Promise.reject(
                      new Error(`El periodo contable hasta el ${lockDay.format('DD/MM/YYYY')} se encuentra cerrado.`)
                    );
                  }
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <DatePicker
            style={{ width: '100%' }}
            format="YYYY-MM-DD"
            disabledDate={(current) => {
              if (!lockDate || !current) return false;
              return current.isBefore(dayjs(lockDate), 'day') || current.isSame(dayjs(lockDate), 'day');
            }}
          />
        </Form.Item>

        <Form.Item label="Referencia / Soporte Bancario" name="reference">
          <Input placeholder="Ej: Transf. Bancolombia #83920 / Comprobante Nequi M-928" />
        </Form.Item>

        <Form.Item
          label="Adjuntar Comprobantes / Soportes"
          tooltip="Puedes adjuntar fotos o capturas de la transferencia (Daviplata, Nequi, Banco) o recibos en formato PDF"
        >
          <Upload
            fileList={fileList}
            beforeUpload={(file: RcFile) => {
              const isAllowed =
                file.type.startsWith('image/') ||
                file.type === 'application/pdf';
              if (!isAllowed) {
                message.error('Solo se permiten imágenes (JPG, PNG, WebP) o archivos PDF.');
                return Upload.LIST_IGNORE;
              }
              const isLt10M = file.size / 1024 / 1024 < 10;
              if (!isLt10M) {
                message.error('El comprobante no debe superar los 10MB.');
                return Upload.LIST_IGNORE;
              }
              return false;
            }}
            onChange={({ fileList: newFileList }) => setFileList(newFileList)}
            multiple
            listType="picture"
            maxCount={5}
          >
            <Button icon={<UploadOutlined />}>Seleccionar Comprobante(s)</Button>
          </Upload>
        </Form.Item>

        <Form.Item label="Observaciones o Notas" name="notes">
          <Input.TextArea rows={2} placeholder="Detalle o motivo de la transacción..." />
        </Form.Item>
      </Form>
    </Modal>

    <UnifiedContactModal
      visible={partnerModalOpen}
      onCancel={() => setPartnerModalOpen(false)}
      onSuccess={(newContact) => {
        setPartnerModalOpen(false);
        if (newContact?.id) {
          form.setFieldsValue({ contact_id: newContact.id });
        }
      }}
      onSave={handleSavePartnerContact}
      role="PARTNER"
      validationMode="strict"
    />
  </>
  );
};

export default CreateTreasuryMovementModal;